import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SpaceLayout from "./pages/SpaceLayout";
import UserProfilePage from "./pages/UserProfilePage";
import SpacePage from "./pages/SpacePage";
import SpaceSettingsPage from "./pages/SpaceSettingsPage";
import PageViewPage from "./pages/PageViewPage";
import NewPagePage from "./pages/NewPagePage";
import PageEditPage from "./pages/PageEditPage";
import AgentsPage from "./pages/AgentsPage";
import ReviewsPage from "./pages/ReviewsPage";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import SetupPage from "./pages/SetupPage";
import AboutPage from "./pages/AboutPage";
import VersionHistoryPage from "./pages/VersionHistoryPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import { getToken, setToken } from "./lib/auth";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = getToken();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Handles /:username/:spaceSlug/* — dispatches to view/edit/history by suffix */
function PageRouter() {
  const rest = (useParams() as Record<string, string>)["*"] ?? "";

  if (rest.endsWith("/edit")) {
    const pageSlug = rest.slice(0, -5);
    return (
      <RequireAuth>
        <PageEditPage pageSlug={pageSlug} />
      </RequireAuth>
    );
  }
  if (rest.endsWith("/history")) {
    const pageSlug = rest.slice(0, -8);
    return <VersionHistoryPage pageSlug={pageSlug} />;
  }
  return <PageViewPage pageSlug={rest} />;
}

/** OAuthコールバック後の ?token= をlocalStorageに保存してURLからクリアする */
function TokenHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token) {
      setToken(token);
      params.delete("token");
      navigate(location.pathname, { replace: true });
    }
  }, []);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <TokenHandler />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminPage />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/agents"
          element={
            <RequireAuth>
              <AgentsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reviews"
          element={
            <RequireAuth>
              <ReviewsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        <Route
          path="/setup"
          element={
            <RequireAuth>
              <SetupPage />
            </RequireAuth>
          }
        />
        <Route path="/:username" element={<UserProfilePage />} />
        <Route path="/:username/:spaceSlug" element={<SpaceLayout />}>
          <Route index element={<SpacePage />} />
          <Route
            path="settings"
            element={
              <RequireAuth>
                <SpaceSettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="new"
            element={
              <RequireAuth>
                <NewPagePage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<PageRouter />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
